<template>
  <transition name="fade">
    <div
      v-if="show"
      class="fullscreen-view"
      @click="close"
    >
      <div class="fullscreen-view__container">
        <Splide
          v-if="isSplideReady && slidesWithThumbnails.length"
          ref="splide"
          :options="splideOptions"
          @splide:move="onSlideSwitch"
        >
          <SplideSlide
            v-for="slide in slidesWithThumbnails"
            :key="slide.id"
            class="fullscreen-view__slide"
          >
            <!-- ✅ YouTube -->
            <template v-if="slide.isYoutube">
              <iframe
                :src="getPictureYoutubeSrc(slide)"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
                class="fullscreen-view__slide-video"
              ></iframe>
            </template>

            <!-- ✅ PDF -->
            <template v-else-if="slide.isPdf">
              <img
                :src="slide.srcThumbnail"
                alt="PDF Thumbnail"
                class="fullscreen-view__slide-image"
                @click.stop="openPdf(slide.srcXl)"
              />
            </template>

            <!-- ✅ Normal image -->
            <template v-else>
              <img
                :src="slide.srcXl"
                alt="image"
                class="fullscreen-view__slide-image"
                @click.stop
              />
            </template>
          </SplideSlide>
        </Splide>

        <!-- Controls -->
        <button
          class="btn btn-icon btn-icon--is-small fullscreen-view__control fullscreen-view__close"
          @click="close"
        >
          <app-icon icon="close" />
        </button>

        <button
          class="btn btn-icon btn-icon--is-small fullscreen-view__control fullscreen-view__previous-image"
          @click.stop="previousPicture"
        >
          <app-icon icon="arrow-left" />
        </button>

        <button
          class="btn btn-icon btn-icon--is-small fullscreen-view__control fullscreen-view__next-image"
          @click.stop="nextPicture"
        >
          <app-icon icon="arrow-left" />
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";
import getPictureYoutubeSrc from "@/helpers/getPictureYoutubeSrc";
import stopEmbeddedVideos from "@/helpers/stopEmbeddedVideos";
import { getPdfThumbnail } from "@/helpers/pdf-thumbnail";

export default {
  name: "FullscreenImage",
  data() {
    return {
      isSplideReady: false,
      splideOptions: {
        type: "loop",
        arrows: false,
        pagination: false,
        rewind: false,
        gap: "20px",
        start: 0,
      },
      getPictureYoutubeSrc,
      slidesWithThumbnails: [],
    };
  },
  computed: {
    ...mapState("Coordinates", ["fullscreenImage"]),
    ...mapGetters("Coordinates", ["getCoordinateById"]),

    show() {
      return this.fullscreenImage.coordinateId !== null;
    },

    coordinate() {
      return this.show
        ? this.getCoordinateById(this.fullscreenImage.coordinateId)
        : null;
    },

    slides() {
      if (!this.coordinate) return [];
      const allSlides = [];

      this.coordinate.allImages.forEach((img) => {
        if (img.srcType === "pdf_file" && Array.isArray(img.src)) {
          img.src.forEach((pdfUrl, index) => {
            allSlides.push({
              id: `${img.id}-pdf-${index}`,
              srcXl: pdfUrl,
              isPdf: true,
              imageId: img.id,
            });
          });
        } else if (Array.isArray(img.pictures)) {
          img.pictures.forEach((pic) => allSlides.push(pic));
        }
      });

      return allSlides;
    },

    currentPictureId: {
      get() {
        return this.fullscreenImage.pictureId || null;
      },
      set(id) {
        this.setFullscreenImage({ pictureId: id });
      },
    },

    currentSlideIndex: {
      get() {
        return this.slides.findIndex(({ id }) => id === this.currentPictureId);
      },
      set(index) {
        this.currentPictureId = this.slides[index].id;
      },
    },
  },

  watch: {
    "fullscreenImage.coordinateId": {
      immediate: true,
      async handler(newValue) {
        if (newValue !== null) {
          await this.prepareSlidesWithThumbnails();

          const clickedIndex = this.slidesWithThumbnails.findIndex(
            (slide) => slide.id === this.fullscreenImage.pictureId
          );

          this.splideOptions = {
            ...this.splideOptions,
            start: clickedIndex !== -1 ? clickedIndex : 0,
          };

          this.isSplideReady = true; 
        } else {
          this.slidesWithThumbnails = [];
        }
      },
    },
  },

  methods: {
    async prepareSlidesWithThumbnails() {
      if (!this.slides?.length) return;

      const processed = await Promise.all(
        this.slides.map(async (slide) => {
          if (slide.isPdf && slide.srcXl.endsWith(".pdf")) {
            try {
              const thumbnail = await getPdfThumbnail(slide.srcXl, 1);
              return { ...slide, srcThumbnail: thumbnail };
            } catch (err) {
              console.error("PDF thumbnail failed:", err);
              return slide;
            }
          }
          return slide;
        })
      );

      this.slidesWithThumbnails = processed;
    },

    onSlideSwitch(_, newSlideIndex) {
      this.currentSlideIndex = newSlideIndex;
      stopEmbeddedVideos(".fullscreen-view__slide-video");
    },

    openPdf(pdfUrl) {
      window.open(pdfUrl, "_blank");
    },

    close() {
      this.isSplideReady = false;
      setTimeout(() => this.setFullscreenImage(null));
    },

    previousPicture() {
      this.$refs.splide?.go("-1");
    },

    nextPicture() {
      this.$refs.splide?.go("+1");
    },

    ...mapActions("Coordinates", ["setFullscreenImage"]),
  },
};
</script>



<style lang="scss">
.fullscreen-view {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.92);
  z-index: 100;

  &__container {
    position: relative;
    width: calc(100vw - 200px);
    height: 90dvh;

    .splide,
    .splide__track,
    .splide__list {
      height: 100%;
    }
  }

  &__slide {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__slide-video {
    width: 100%;
    height: 100%;
  }

  &__slide-image {
    max-width: 100%;
    max-height: 100%;
  }

  &__control {
    position: absolute;
  }

  &__close {
    top: 0;
    left: calc(100% + 30px);
  }

  &__previous-image,
  &__next-image {
    top: 50%;
    transform: translateY(-50%);
  }
  &__previous-image {
    right: calc(100% + 30px);
  }
  &__next-image {
    left: calc(100% + 30px);

    .app-icon {
      transform: rotate(180deg);
    }
  }
}

@include smallerThan("xl") {
  .fullscreen-view {
    &__container {
      position: relative;
      width: 90vw;
      height: calc(100dvh - 200px);
    }

    &__close {
      top: auto;
      left: auto;
      bottom: calc(100% + 30px);
      right: 0;
    }

    &__previous-image,
    &__next-image {
      transform: none;
      top: calc(100% + 30px);
    }

    &__previous-image {
      right: auto;
      left: 0;
    }
    &__next-image {
      left: auto;
      right: 0;
    }
  }
}
</style>
